import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router";

import { map } from "lodash";
import Lightbox from "./Lightbox/Lightbox";

import Checkbox from "./Forms/Checkbox";

import {
  faExpand,
  faPencilAlt,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmationModal from "./ConfirmationModal";
import "./Gallery.css";

const Gallery = ({
  heading,
  subheading,
  id,
  images,
  lightboxImages,
  hideMetadata,
  hideEditButton,
  hideExpandButton,
  checkboxText,
  isAuthorizedForRediger,
  isAuthorizedToDeleteImage,
  handleCheckboxChange,
  handleDeleteImageClick,
  getRedigerUrl,
  getBildeUrl,
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const openLightbox = useCallback((index, event) => {
    event.preventDefault();
    setCurrentImage(index);
    setIsLightboxOpen(true);

    // Ingen scroll
    const htmlElement = document.getElementsByTagName("html");
    if (htmlElement && htmlElement.length > 0) {
      htmlElement[0].style.overflow = "hidden";
    }
    const bodyElement = document.getElementsByTagName("body");
    if (bodyElement && bodyElement.length > 0) {
      bodyElement[0].style.overflow = "hidden";
    }
  }, []);

  const closeLightbox = useCallback(() => {
    setCurrentImage(0);
    setIsLightboxOpen(false);

    // Scroll
    const htmlElement = document.getElementsByTagName("html");
    if (htmlElement && htmlElement.length > 0) {
      htmlElement[0].style.overflow = "";
    }
    const bodyElement = document.getElementsByTagName("body");
    if (bodyElement && bodyElement.length > 0) {
      bodyElement[0].style.overflow = "";
    }
  }, []);

  const gotoPrevious = useCallback(() => {
    setCurrentImage((prev) => {
      if (prev <= 0) return lightboxImages ? lightboxImages.length - 1 : 0;
      return prev - 1;
    });
  }, [lightboxImages]);

  const gotoNext = useCallback(() => {
    setCurrentImage((prev) => {
      if (prev >= (lightboxImages ? lightboxImages.length - 1 : 0)) return 0;
      return prev + 1;
    });
  }, [lightboxImages]);

  const getDeleteButton = (handleClickShow) => (
    <Button
      className="btn-bym-standard"
      style={{
        width: 42,
        height: 30,
        paddingTop: 6,
        paddingRight: 12,
        paddingBottom: 6,
        paddingLeft: 12,
      }}
      onClick={handleClickShow}
    >
      <FontAwesomeIcon icon={faTrashAlt} />
    </Button>
  );

  const renderGallery = (imgs, hideMetaData, hideEdit, hideExpand) => {
    const gallery = map(imgs, (obj, index) => (
      <div key={index} style={{ display: "inline-block", paddingRight: 4 }}>
        {obj.src && (
          <>
            <div
              style={{
                position: "relative",
                display: "block",
                paddingBottom: 4,
              }}
            >
              <div
                className={`gallery-thumbnail${obj.orientation ? ` ${obj.orientation}` : ""}`}
              >
                <div className="gallery-image-wrapper">
                  <a
                    href={obj.src}
                    onClick={(event) => openLightbox(index, event)}
                  >
                    <img
                      alt=""
                      src={obj.src}
                      style={{
                        width: "auto",
                        height: "142px",
                        margin: "auto",
                        border: 0,
                        display: "block",
                        maxWidth: "100%",
                      }}
                    />
                  </a>
                </div>
                <div
                  className="image-buttons-container"
                  style={{ position: "absolute" }}
                >
                  {isAuthorizedForRediger && !hideEdit && (
                    <div
                      style={{ marginTop: 4, marginRight: 4, marginLeft: 4 }}
                    >
                      <span title="Rediger">
                        <Link
                          to={
                            getRedigerUrl
                              ? getRedigerUrl(obj.id)
                              : `bilder/${obj.id}/rediger`
                          }
                        >
                          <Button
                            className="btn-bym-standard"
                            style={{
                              width: 42,
                              height: 30,
                              paddingTop: 6,
                              paddingRight: 12,
                              paddingBottom: 6,
                              paddingLeft: 12,
                            }}
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </Button>
                        </Link>
                      </span>
                    </div>
                  )}
                  {!hideExpand && (
                    <div
                      style={{ marginTop: 4, marginRight: 4, marginLeft: 4 }}
                    >
                      <span title="Se full størrelse">
                        <Button
                          className="btn-bym-standard"
                          style={{
                            width: 42,
                            height: 30,
                            paddingTop: 6,
                            paddingRight: 12,
                            paddingBottom: 6,
                            paddingLeft: 12,
                          }}
                          onClick={() => {
                            const url = getBildeUrl
                              ? getBildeUrl(obj.id)
                              : `${window.location.pathname}/bilder/${obj.id}`;
                            window.open(url);
                          }}
                        >
                          <FontAwesomeIcon icon={faExpand} />
                        </Button>
                      </span>
                    </div>
                  )}
                  {isAuthorizedToDeleteImage &&
                    obj.opprettetAvRolle === "Saksbehandler" && (
                      <div style={{ marginTop: 4 }}>
                        <span title="Slett">
                          <ConfirmationModal
                            body="Vil du slette dette bildet?"
                            confirmText="Slett"
                            title="Slett bilde"
                            onConfirm={() => handleDeleteImageClick(obj.id)}
                            deleteButtonOpenModal={getDeleteButton}
                          />
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>
            {!hideMetaData && (
              <div className="gallery-image-meta-data-wrapper">
                <div className="gallery-file-name-wrapper">
                  {obj.navn ? obj.navn : "filenavn.jpeg"}
                </div>
                <div className="gallery-file-name-wrapper">
                  {obj.opprettetAvRolle}
                </div>
                {(checkboxText ||
                  typeof handleCheckboxChange !== typeof undefined) && (
                  <div className="gallery-checkbox-wrapper">
                    <Checkbox
                      id={obj.id}
                      name="tilEttersendelse"
                      checkedValue={true}
                      uncheckedValue={false}
                      currentValue={obj.isGoodQuality}
                      handleChange={handleCheckboxChange}
                      text={checkboxText}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {!obj.src && (
          <div
            style={{
              position: "relative",
              display: "block",
              paddingBottom: 4,
            }}
          >
            <div style={{ width: 200, height: 150 }}>
              ⚠️ {obj.errorMessage} ⚠️
            </div>
          </div>
        )}
      </div>
    ));

    return <div className="gallery">{gallery}</div>;
  };

  if (!images) return null;

  return (
    <div className={id}>
      {heading && (
        <h3
          className="GalleryHeadingText fake-label"
          style={{ fontSize: 19, fontWeight: "bold" }}
        >
          {heading}
        </h3>
      )}
      {subheading && <p className="GallerySubHeadingText">{subheading}</p>}
      <div>
        {renderGallery(images, hideMetadata, hideEditButton, hideExpandButton)}
      </div>
      {lightboxImages && lightboxImages.length > 0 && (
        <Lightbox
          showLightbox={isLightboxOpen}
          images={lightboxImages}
          imageIndex={currentImage}
          onNext={gotoNext}
          onPrevious={gotoPrevious}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
};

Gallery.displayName = "Gallery";
Gallery.propTypes = {
  heading: PropTypes.string,
  images: PropTypes.array,
  lightboxImages: PropTypes.array,
  showThumbnails: PropTypes.bool,
  subheading: PropTypes.string,
  id: PropTypes.string,
  theme: PropTypes.string,
  checkboxText: PropTypes.string,
  isAuthorizedForRediger: PropTypes.bool,
  isAuthorizedToDeleteImage: PropTypes.bool,
  hideMetadata: PropTypes.bool,
  hideEditButton: PropTypes.bool,
  hideExpandButton: PropTypes.bool,
  handleCheckboxChange: PropTypes.func,
  handleDeleteImageClick: PropTypes.func,
  getRedigerUrl: PropTypes.func,
  getBildeUrl: PropTypes.func,
};

export default Gallery;
